/**
 * @typedef {Object} TPathSegments
 * @property {string} start
 * @property {string} end
 * @property {string} with
 * @property {string} length
 *
 * @typedef {Object} TInterlinkModuleItem
 * @property {string} anchorTitle
 * @property {string} anchorText
 * @property {string} path
 *
 * @typedef {Object} TInterlinkModule
 * @property {string} title
 * @property {Array<TInterlinkModuleItem>} items
 *
 * @typedef {Object} TGenerateInterlinkModuleParams
 * @property {string} whitelist
 * @property {string} pathTemplate
 * @property {string} titleTemplate
 * @property {TPathSegments} pathSegments
 *
 */

/**
 * Make replacements on a given template string, by using the segments passed
 *
 * @param {{ template: string, pathSegments: TPathSegments }} params
 * @returns {string}
 */
const buildString = ({ template, pathSegments = {} }) => {
	let result = template;

	for (const key in pathSegments) {
		let replacement = pathSegments[key];
		if (isNaN(replacement)) {
			replacement = replacement?.toUpperCase();
		}
		if (!result) {
			result = "";
		}
		result = result.replace(`%${key}`, replacement);
	}

	return result;
};

/**
 * Builds the actual interlink module, by generating a link for each element found in the whitelist
 *
 * @param {{ anchorTitlePrefix: string, pathTemplate: string, pathSegments: TPathSegments, whitelist: string }} params
 * @returns {TInterlinkModuleItem}
 */
const buildItems = ({
	anchorTitlePrefix,
	pathTemplate,
	pathSegments,
	whitelist,
	query,
}) => {
	const items = [];

	if (typeof whitelist !== "string" || whitelist.length === 0) {
		return [];
	}

	for (let whitelistElement of whitelist.split(",")) {
		if (isNaN(whitelistElement)) {
			whitelistElement = whitelistElement.toUpperCase();
		}

		let path = `${buildString({
			template: pathTemplate,
			pathSegments,
			query,
		}).replace("%_dynamic", whitelistElement)}`.toLowerCase();

		if (query && Object.keys(query).length) {
			path += `?${new URLSearchParams(query)}`;
		}

		items.push({
			anchorTitle: `${anchorTitlePrefix} ${whitelistElement}`,
			anchorText: whitelistElement.toString().toUpperCase(),
			path,
		});
	}

	return items;
};

/**
 * Generates an interlink module, given a whitelist, a path and title templates, and an object of path segments representing value to be replaced in the placeholders defined on the templates
 *
 * @callback TGenerateInterlinkModule
 * @param {TGenerateInterlinkModuleParams} params
 * @returns {TInterlinkModule}
 */
export const generateInterlinkModule = ({
	whitelist,
	pathTemplate,
	titleTemplate,
	pathSegments,
	query,
}) => {
	const title = buildString({ template: titleTemplate, pathSegments });
	const items = buildItems({
		anchorTitlePrefix: title,
		pathTemplate,
		pathSegments,
		whitelist,
		query,
	});

	return {
		title,
		items,
	};
};
